import React, { useState } from "react"
import {
  Package,
  ArrowRight,
  Plane,
  Truck,
  FileCheck,
  Building,
  CheckCircle,
  Clock,
  Compass,
  MapPin,
  FileText,
  User,
  Activity,
  Layers,
  ChevronDown,
  ChevronUp,
} from "lucide-react"

interface TrackingEvent {
  _id: string
  sNo: number
  status?: string
  activityType?: string
  date?: string | null
  time?: string
  from?: string
  to?: string
  location?: string
  driverName?: string
  vehicle?: string
  warehouse?: string
  createdAt?: string
  blOrAwb?: string
  awb?: string
}

interface TrackingResult {
  pickup?: Array<{ _id: number | string; events: TrackingEvent[] }>
  OC?: TrackingEvent[]
  freight?: TrackingEvent[]
  DC?: TrackingEvent[]
  delivery?: Array<{ _id: number | string; events: TrackingEvent[] }>
  shipmentData?: {
    jobNo?: string
    referenceNo?: string
    customerName?: string
    clearenceDate?: string
    commodity?: string
    chargeableWeight?: number
    grossWeight?: number
    volumeWeight?: number
    totalPieces?: number
    incoTerms?: string
    originAirport?: { code: string; name: string }
    originDoor?: { city: string; country: string }
    destinationAirport?: { code: string; name: string }
    destinationDoor?: { city: string; country: string }
  }
}

interface TrackingDetailsDisplayProps {
  trackingData: {
    success: boolean
    message: string
    result: TrackingResult
  }
}

export default function TrackingDetailsDisplay({ trackingData }: TrackingDetailsDisplayProps) {
  const [activeTab, setActiveTab] = useState<"timeline" | "raw">("timeline")
  const [expandedSection, setExpandedSection] = useState<string | null>(null)

  if (!trackingData || !trackingData.success || !trackingData.result) {
    return (
      <div className="rounded-xl border border-amber-100 bg-amber-50/50 p-4 text-sm text-amber-700 font-sans">
        <p className="font-semibold">Tracking Information Unavailable</p>
        <p className="text-xs text-amber-600 mt-0.5">The tracking provider did not return any update events yet.</p>
      </div>
    )
  }

  const { result } = trackingData
  const shipment = result.shipmentData || {}

  // Extract events from pickup/delivery arrays
  const pickupEvents: TrackingEvent[] = (result.pickup || [])
    .flatMap((p) => p.events || [])
    .sort((a, b) => a.sNo - b.sNo)

  const ocEvents: TrackingEvent[] = (result.OC || []).sort((a, b) => a.sNo - b.sNo)
  const freightEvents: TrackingEvent[] = (result.freight || []).sort((a, b) => a.sNo - b.sNo)
  const dcEvents: TrackingEvent[] = (result.DC || []).sort((a, b) => a.sNo - b.sNo)
  
  const deliveryEvents: TrackingEvent[] = (result.delivery || [])
    .flatMap((d) => d.events || [])
    .sort((a, b) => a.sNo - b.sNo)

  const allStages = [
    { id: "pickup", name: "1. Pickup Stage", events: pickupEvents, icon: Truck, color: "text-blue-500 bg-blue-50 border-blue-100" },
    { id: "oc", name: "2. Origin Clearance", events: ocEvents, icon: FileCheck, color: "text-purple-500 bg-purple-50 border-purple-100" },
    { id: "freight", name: "3. Freight/Transit", events: freightEvents, icon: Plane, color: "text-indigo-500 bg-indigo-50 border-indigo-100" },
    { id: "dc", name: "4. Destination Clearance", events: dcEvents, icon: Building, color: "text-amber-500 bg-amber-50 border-amber-100" },
    { id: "delivery", name: "5. Delivery Stage", events: deliveryEvents, icon: CheckCircle, color: "text-emerald-500 bg-emerald-50 border-emerald-100" },
  ]

  // Calculate overall status badge
  let currentStatus = "In Transit"
  let latestEvent: TrackingEvent | null = null
  
  if (deliveryEvents.length > 0) {
    latestEvent = deliveryEvents[deliveryEvents.length - 1]
    currentStatus = latestEvent.status || latestEvent.activityType || "Delivering"
  } else if (dcEvents.length > 0) {
    latestEvent = dcEvents[dcEvents.length - 1]
    currentStatus = latestEvent.activityType || "Destination Clearance"
  } else if (freightEvents.length > 0) {
    latestEvent = freightEvents[freightEvents.length - 1]
    currentStatus = latestEvent.activityType || "In Air/Ocean Transit"
  } else if (ocEvents.length > 0) {
    latestEvent = ocEvents[ocEvents.length - 1]
    currentStatus = latestEvent.activityType || "Origin Clearance"
  } else if (pickupEvents.length > 0) {
    latestEvent = pickupEvents[pickupEvents.length - 1]
    currentStatus = latestEvent.status || latestEvent.activityType || "Picked Up"
  }

  const toggleSection = (id: string) => {
    setExpandedSection(expandedSection === id ? null : id)
  }

  function formatDateTime(dateStr?: string | null, timeStr?: string) {
    if (!dateStr && !timeStr) return "Pending"
    const datePart = dateStr ? new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""
    const timePart = timeStr ? timeStr : ""
    return `${datePart} ${timePart}`.trim()
  }

  return (
    <div className="font-sans text-left space-y-5 animate-in fade-in duration-200">
      {/* Overview Dashboard Card */}
      <div className="group relative overflow-hidden bg-white border border-[#be001e]/15 hover:border-[#be001e]/30 rounded-2xl p-5 shadow-sm transition-all duration-300">
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#be001e] to-rose-500 rounded-t-2xl" />

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="space-y-1">
            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Cargo Reference</span>
            <h3 className="text-base font-extrabold text-slate-800 tracking-tight flex items-center gap-2">
              <Compass className="size-4.5 text-[#e6202f]" />
              {shipment.referenceNo || shipment.jobNo || "Zipaworld Express Shipment"}
            </h3>
          </div>

          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold bg-[#be001e]/5 text-[#be001e] border border-[#be001e]/10 uppercase tracking-wide">
              <Activity className="size-3 animate-pulse" />
              {currentStatus}
            </span>
            {shipment.incoTerms && (
              <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-bold bg-slate-100 text-slate-700 border border-slate-200">
                {shipment.incoTerms}
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5 pt-4 border-t border-slate-100 text-xs">
          <div>
            <span className="text-slate-400 font-semibold block mb-0.5">Commodity</span>
            <span className="font-bold text-slate-800">{shipment.commodity || "General Cargo"}</span>
          </div>
          <div>
            <span className="text-slate-400 font-semibold block mb-0.5">Weight (Chg / Gross)</span>
            <span className="font-bold text-slate-800 font-mono">
              {shipment.chargeableWeight || "—"} kg / {shipment.grossWeight || "—"} kg
            </span>
          </div>
          <div>
            <span className="text-slate-400 font-semibold block mb-0.5">Total Pieces</span>
            <span className="font-bold text-slate-800 font-mono">{shipment.totalPieces || "—"} Pcs</span>
          </div>
          <div>
            <span className="text-slate-400 font-semibold block mb-0.5">Customer</span>
            <span className="font-bold text-slate-800 truncate block">{shipment.customerName || "—"}</span>
          </div>
        </div>

        {/* Origin / Destination flow visualization */}
        <div className="mt-5 p-3.5 bg-slate-50/60 border border-slate-100 rounded-xl flex flex-col sm:flex-row justify-between items-center gap-3">
          <div className="text-center sm:text-left">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Origin</span>
            <span className="font-extrabold text-slate-800 text-sm">
              {shipment.originAirport?.code || shipment.originDoor?.city || "Source"}
            </span>
            <span className="text-[10px] text-slate-500 block truncate max-w-[200px]">
              {shipment.originAirport?.name || shipment.originDoor?.country}
            </span>
          </div>

          <div className="flex flex-col items-center gap-1.5 flex-1 max-w-[120px]">
            <div className="h-px bg-slate-300 w-full relative flex items-center justify-center">
              <Plane className="size-4 text-[#e6202f] absolute bg-slate-50 p-0.5 rounded-full border border-slate-200" />
            </div>
            <span className="text-[9px] font-bold text-[#e6202f] tracking-widest uppercase">Air Freight</span>
          </div>

          <div className="text-center sm:text-right">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Destination</span>
            <span className="font-extrabold text-slate-800 text-sm">
              {shipment.destinationAirport?.code || shipment.destinationDoor?.city || "Destination"}
            </span>
            <span className="text-[10px] text-slate-500 block truncate max-w-[200px]">
              {shipment.destinationAirport?.name || shipment.destinationDoor?.country}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200">
        <button
          onClick={() => setActiveTab("timeline")}
          className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors cursor-pointer ${
            activeTab === "timeline"
              ? "border-[#be001e] text-[#be001e]"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          Visual Timeline
        </button>
        <button
          onClick={() => setActiveTab("raw")}
          className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors cursor-pointer ${
            activeTab === "raw"
              ? "border-[#be001e] text-[#be001e]"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          JSON Payload
        </button>
      </div>

      {activeTab === "timeline" ? (
        <div className="space-y-3">
          {allStages.map((stage) => {
            const hasEvents = stage.events.length > 0
            const isExpanded = expandedSection === stage.id
            const StageIcon = stage.icon

            return (
              <div
                key={stage.id}
                className="bg-white border border-slate-100 hover:border-slate-200 rounded-xl shadow-sm overflow-hidden transition-all duration-200"
              >
                {/* Header block clickable to expand/collapse details */}
                <div
                  onClick={() => hasEvents && toggleSection(stage.id)}
                  className={`flex items-center justify-between p-4 cursor-pointer select-none transition-colors ${
                    hasEvents ? "hover:bg-slate-50/50" : "opacity-60 cursor-not-allowed"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg border flex items-center justify-center ${stage.color}`}>
                      <StageIcon className="size-4.5" />
                    </div>
                    <div>
                      <h4 className="text-xs font-extrabold text-slate-800">{stage.name}</h4>
                      <p className="text-[10px] text-slate-400 font-medium">
                        {hasEvents
                          ? `${stage.events.length} update event${stage.events.length !== 1 ? "s" : ""} log`
                          : "No event logged for this milestone"}
                      </p>
                    </div>
                  </div>

                  {hasEvents && (
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-100 px-2 py-0.5 rounded">
                        Active
                      </span>
                      {isExpanded ? (
                        <ChevronUp className="size-4 text-slate-500" />
                      ) : (
                        <ChevronDown className="size-4 text-slate-500" />
                      )}
                    </div>
                  )}
                </div>

                {/* Event timeline details list */}
                {isExpanded && hasEvents && (
                  <div className="border-t border-slate-100 bg-slate-50/20 p-4 space-y-4 animate-in slide-in-from-top-2 duration-200">
                    <div className="relative pl-5 border-l border-slate-200 ml-2 space-y-5 py-1">
                      {stage.events.map((evt, idx) => {
                        const statusTitle = evt.status || evt.activityType || "Milestone Cleared"
                        const isLatest = idx === stage.events.length - 1

                        return (
                          <div key={evt._id || idx} className="relative flex flex-col gap-1 text-xs">
                            {/* Bullet icon */}
                            <div className={`absolute -left-[26px] top-0 flex items-center justify-center size-3.5 rounded-full border bg-white ${
                              isLatest ? "border-emerald-500" : "border-slate-300"
                            }`}>
                              <div className={`size-1.5 rounded-full ${
                                isLatest ? "bg-emerald-500 animate-ping" : "bg-slate-400"
                              }`} />
                            </div>

                            {/* Event content */}
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <span className="font-extrabold text-slate-800">{statusTitle}</span>
                              <span className="text-[10px] font-mono text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                                {formatDateTime(evt.date, evt.time)}
                              </span>
                            </div>

                            {/* Extra event meta properties */}
                            {(evt.from || evt.to || evt.location) && (
                              <div className="text-[11px] text-slate-500 font-medium flex items-center gap-1.5 mt-0.5">
                                <MapPin className="size-3.5 text-slate-400" />
                                {evt.location || `${evt.from || "Origin"} → ${evt.to || "Destination"}`}
                              </div>
                            )}

                            {(evt.driverName || evt.vehicle) && (
                              <div className="text-[10px] text-slate-400 font-semibold flex flex-wrap gap-3 mt-1 bg-white p-2 rounded-lg border border-slate-100 max-w-sm">
                                {evt.driverName && (
                                  <span className="flex items-center gap-1">
                                    <User className="size-3" /> Driver: {evt.driverName}
                                  </span>
                                )}
                                {evt.vehicle && (
                                  <span className="flex items-center gap-1">
                                    <Truck className="size-3" /> Vehicle: {evt.vehicle}
                                  </span>
                                )}
                                {evt.warehouse && (
                                  <span className="flex items-center gap-1">
                                    <Building className="size-3" /> Hub: {evt.warehouse}
                                  </span>
                                )}
                              </div>
                            )}

                            {(evt.blOrAwb || evt.awb) && (
                              <div className="text-[10px] text-[#e6202f] font-mono font-bold mt-1">
                                AWB/BL Ref: {evt.blOrAwb || evt.awb}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      ) : (
        <div className="bg-slate-900 rounded-xl p-4 border border-slate-800 font-mono text-xs overflow-auto max-h-[420px] text-slate-300">
          <pre>{JSON.stringify(trackingData, null, 2)}</pre>
        </div>
      )}
    </div>
  )
}
